import './styles.scss';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Handle, Position } from '@xyflow/react';
import { Skeleton, Tooltip } from '@mui/material';
import { IconPencil, IconTrash, IconSchool, IconArrowNarrowRight } from '@tabler/icons-react';
import { Text, Button } from '@fragments';
import StatusChip from '@fragments/StatusChip';
import { UserAvatar } from '@components/Community';
import useMentoringProgram from '@pages/CommunityConfig/tabs/Split/hooks/useMentoringProgram';
import { formatProjectedPct } from '@pages/CommunityConfig/tabs/Split/helpers/projectSplitAcceptance';
import { getIntlLocale } from '@i18n';

/**
 * "será de X%" — the receiver's renormalized share once the pending invite(s) are
 * accepted. Rendered only when a projection differs from the current value.
 */
function ProjectionLine({ projectedPercentile }) {
    const { t } = useTranslation('settings');
    if (projectedPercentile == null) return null;
    return (
        <Text.span
            contrast={6}
            className="srn-projection"
            left={<IconArrowNarrowRight size={13} />}
        >
            {t('split.flow.receiver.projection', { value: formatProjectedPct(projectedPercentile) })}
        </Text.span>
    );
}

// International (Stripe) sales are NOT split — receivers apply to national
// (Asaas/BRL) sales only, so a receiver's Stripe-connection state is
// irrelevant here (the old "Intl conectada"/"Sem conta intl" badges were
// removed with the no-split decision; SplitCurrencyNotice states the rule).

const MENTORING_TYPE = 'mentoring';

function readEmail(user) {
    return (user?.email?.address || user?.email || '').trim().toLowerCase();
}

// Mentoring → "Mentoria" (takes precedence); the signed-in user's own entry →
// "Você"; everyone else → "Coprodutor". Mentoring entries belong to the program
// owner, never the viewer, so the precedence is unambiguous.
function resolvePill(user, currentUserEmail, t) {
    if (user?.type === MENTORING_TYPE) {
        return { text: t('split.flow.receiver.pill.mentoring'), variant: 'info' };
    }

    const email = readEmail(user);
    const me = (currentUserEmail || '').trim().toLowerCase();

    if (email && me && email === me) {
        return { text: t('split.flow.receiver.pill.you'), variant: 'success' };
    }

    return { text: t('split.flow.receiver.pill.coproducer'), variant: 'default' };
}

function formatExpiration(iso) {
    if (!iso) return null;
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString(getIntlLocale(), {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });
}

/** Editable percentile control (coproduction / "Você" entries). */
function PercentEditor({ user, onChangePercentile, onRemoveReceiver }) {
    const { t } = useTranslation('settings');
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(user.percentile ?? 0);
    const inputRef = useRef(null);

    useEffect(() => {
        if (editing) inputRef.current?.select();
    }, [editing]);

    function commit() {
        const value = Math.min(100, Math.max(0, parseInt(draft, 10) || 0));
        onChangePercentile?.(user, value);
        setEditing(false);
    }

    function handleKeyDown(event) {
        if (event.key === 'Enter') commit();
        if (event.key === 'Escape') setEditing(false);
    }

    if (editing) {
        return (
            <div className="srn-editor nodrag nopan">
                <input
                    ref={inputRef}
                    type="number"
                    min={0}
                    max={100}
                    value={draft}
                    onChange={event => setDraft(event.target.value)}
                    onBlur={commit}
                    onKeyDown={handleKeyDown}
                />
                <Text.span contrast={5}>%</Text.span>
                {onRemoveReceiver && (
                    <Button
                        className="srn-remove"
                        discreet
                        color="error"
                        size="micro"
                        preventLoading
                        icon={<IconTrash />}
                        tooltip={t('split.flow.receiver.removeTooltip')}
                        onMouseDown={event => event.preventDefault()}
                        onClick={() => onRemoveReceiver(user)}
                    />
                )}
            </div>
        );
    }

    return (
        <div className="srn-percent">
            <Text.h2  contrast={8} className="srn-percent-value">
                {user.percentile ?? 0}%
            </Text.h2>
            <Button
                className="srn-edit"
                discreet
                color="current"
                size="micro"
                preventLoading
                icon={<IconPencil />}
                tooltip={t('split.flow.receiver.editTooltip')}
                onClick={() => {
                    setDraft(user.percentile ?? 0);
                    setEditing(true);
                }}
            />
        </div>
    );
}

/** Read-only percentile (mentoring entries are managed by the Mentoring module). */
function PercentReadonly({ user }) {
    const { t } = useTranslation('settings');

    return (
        <Tooltip arrow title={t('split.flow.receiver.readonlyTooltip')}>
            <div className="srn-percent srn-percent--readonly">
                <Text.h2 contrast={8} className="srn-percent-value">
                    {user.percentile ?? 0}%
                </Text.h2>
            </div>
        </Tooltip>
    );
}

/** Coproduction / "Você" receiver — the user's avatar, name and email. */
function UserReceiver({ user, projectedPercentile }) {
    const displayName = user?.name || user?.username || null;
    const email = user?.email?.address || user?.email || null;

    return (
        <>
            <UserAvatar user={{ ...user, username: displayName }} size={38} />
            <div className="srn-identity">
                <Text.h5 weight={500} contrast={8} className="srn-name">
                    {displayName}
                </Text.h5>
                {email && (
                    <Text.span contrast={5} className="srn-email">
                        {email}
                    </Text.span>
                )}
                <ProjectionLine projectedPercentile={projectedPercentile} />
            </div>
        </>
    );
}

/** Mentoring receiver — loads the program, shows its banner + name. */
function MentoringReceiver({ user }) {
    const { t } = useTranslation('settings');
    const { program, loading } = useMentoringProgram(user.mentoring_enrollment_id);
    const expiration = formatExpiration(user.mentoring_expiration_date);
    const bannerUrl = program?.banner?.url || null;
    const isVideo = program?.banner?.type === 'video';

    return (
        <>
            <div className="srn-banner">
                {loading ? (
                    <Skeleton variant="rounded" width={38} height={38} />
                ) : bannerUrl ? (
                    isVideo ? (
                        <video
                            className="srn-banner-media"
                            src={bannerUrl}
                            muted
                            playsInline
                            preload="metadata"
                        />
                    ) : (
                        <img
                            className="srn-banner-media"
                            src={bannerUrl}
                            alt={program?.name || 'Mentoria'}
                        />
                    )
                ) : (
                    <span className="srn-banner-fallback">
                        <IconSchool size={20} />
                    </span>
                )}
            </div>

            <div className="srn-identity">
                {loading ? (
                    <Skeleton variant="text" width="70%" height={20} />
                ) : (
                    <Text.h5 weight={500} contrast={8} className="srn-name">
                        {program?.name || t('split.flow.receiver.mentoringFallback')}
                    </Text.h5>
                )}
                {expiration && (
                    <Text.span contrast={5} className="srn-email">
                        {t('split.flow.receiver.transferUntil', { date: expiration })}
                    </Text.span>
                )}
            </div>
        </>
    );
}

export default function ReceiverNode({ data }) {
    const { t } = useTranslation('settings');
    const { user, index, currentUserEmail, projectedPercentile, onChangePercentile, onRemoveReceiver } = data;

    const isMentoring = user?.type === MENTORING_TYPE;
    const pill = resolvePill(user, currentUserEmail, t);

    return (
        <div className="split-receiver-node">
            <Handle
                type="target"
                position={Position.Left}
                className="split-touch-handle"
            />

            <div
                className="srn-card"
                style={{ animationDelay: `${index * 70}ms` }}
            >
                <StatusChip
                    text={pill.text}
                    variant={pill.variant}
                    className="srn-pill"
                />

                {isMentoring ? (
                    <MentoringReceiver user={user} />
                ) : (
                    <UserReceiver user={user} projectedPercentile={projectedPercentile} />
                )}

                {isMentoring ? (
                    <PercentReadonly user={user} />
                ) : (
                    <PercentEditor
                        user={user}
                        onChangePercentile={onChangePercentile}
                        onRemoveReceiver={onRemoveReceiver}
                    />
                )}
            </div>
        </div>
    );
}
